import { Client } from '@/pages/user/api';
import { AccountModule } from '@/store/modules/account';
import * as Cache from '@/utils/cache.util';

const apiPrefix = 'portal';

const parseFileNameFromDisposition = (disposition: string | null) => {
  if (!disposition) {
    return '';
  }
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    return decodeURIComponent(utf8Match[1]);
  }
  const normalMatch = disposition.match(/filename="?([^"]+)"?/i);
  if (normalMatch && normalMatch[1]) {
    return decodeURIComponent(normalMatch[1]);
  }
  return '';
};

const readBlobAsText = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob, 'utf-8');
  });

export interface SampleOrderProductDetailQuery {
  productSn: string;
}

export interface SampleOrderCouponRedeemPayload {
  productSn: string;
  sizeOptionSn: string;
  quantity: number;
  couponCode: string;
}

export interface SampleOrderCreatePayload {
  productSn: string;
  sizeOptionSn: string;
  quantity: number;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  contactName: string;
  contactPhone: string;
  provinceCode?: string;
  cityCode?: string;
  districtCode?: string;
  addressDetail: string;
  couponCode?: string;
  remark?: string;
}

export interface SampleOrderCreateResult {
  orderSn: string;
  originalAmount: number;
  couponAmount: number;
  payableAmount: number;
  orderStatus: string;
  payRequired: boolean;
  payChannel?: string;
  outTradeNo?: string;
  qrCodeDataUrl?: string;
  expireAt?: string;
  payStatus?: string;
  couponCode?: string;
  couponMessage?: string;
}

export interface SampleCouponAdminPageQuery {
  keyword?: string;
  status?: string;
  pageNum?: number;
  pageSize?: number;
}

export interface SampleCouponAdminSavePayload {
  id?: number;
  couponCode?: string;
  couponAmount: number;
  quantity?: number;
  expireAt?: string;
  source?: string;
  batchNo?: string;
  remark?: string;
}

export interface SampleCouponAdminStatusPayload {
  id: number;
  status: string;
}

export interface SampleOrderPageQuery {
  keyword?: string;
  tab?: 'ALL' | 'UNPAID' | 'PRODUCING' | 'FINISHED' | 'CANCELED';
  createdAtStart?: string;
  createdAtEnd?: string;
  pageNum: number;
  pageSize: number;
}

export interface PayNativePrepayPayload {
  bizType: string;
  bizOrderSn: string;
}

export interface PayOrderStatusQuery {
  outTradeNo: string;
}

export interface PayOrderClosePayload {
  outTradeNo: string;
}

export interface PayNativePrepayResult {
  bizType: string;
  bizOrderSn: string;
  outTradeNo: string;
  status: string;
  amount: number;
  currency: string;
  description: string;
  codeUrl: string;
  qrCodeDataUrl?: string;
  expireAt?: string;
  orderStatus?: string;
}

export interface PayOrderStatusResult {
  outTradeNo: string;
  bizOrderSn: string;
  status: string;
  tradeState: string;
  paidAt?: string;
  message?: string;
}

export const GetSampleProductDetail = async (data: SampleOrderProductDetailQuery): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/order/sample/productDetail',
    data,
  });
};

export const RedeemSampleCoupon = async (data: SampleOrderCouponRedeemPayload): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/order/sample/coupon/redeem',
    data,
  });
};

export const CreateSampleOrder = async (data: SampleOrderCreatePayload): Promise<SampleOrderCreateResult | null> => {
  return await Client.post<SampleOrderCreateResult>({
    url: apiPrefix + '/order/sample/create',
    data,
  });
};

export const GetSampleOrderPage = async (data: SampleOrderPageQuery): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/order/sample/page',
    data,
  });
};

export const GetSampleCouponAdminPage = async (data: SampleCouponAdminPageQuery): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/order/sample/admin/coupon/pageQuery',
    data,
  });
};

export const CreateSampleCouponAdmin = async (data: SampleCouponAdminSavePayload): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/order/sample/admin/coupon/create',
    data,
  });
};

export const ExportSampleCouponAdmin = (data: SampleCouponAdminPageQuery) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const params = new URLSearchParams();
    if (data.keyword) {
      params.append('keyword', data.keyword);
    }
    if (data.status) {
      params.append('status', data.status);
    }

    const requestUrl = `/portal/order/sample/admin/coupon/export${params.toString() ? `?${params.toString()}` : ''}`;
    xhr.open('GET', requestUrl, true);
    xhr.responseType = 'blob';

    const token = AccountModule.token || Cache.getToken('user');
    if (token) {
      xhr.setRequestHeader('Authorization', token);
    }

    xhr.onload = async () => {
      const contentType = xhr.getResponseHeader('Content-Type') || '';
      if (
        xhr.status >= 200 &&
        xhr.status < 300 &&
        (contentType.indexOf('application/vnd') > -1 || contentType.indexOf('application/octet-stream') > -1)
      ) {
        const fileName = parseFileNameFromDisposition(xhr.getResponseHeader('Content-Disposition')) || 'sample-coupon-export.xls';
        const blobUrl = window.URL.createObjectURL(xhr.response);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        resolve();
        return;
      }

      try {
        const text = await readBlobAsText(xhr.response);
        const res = JSON.parse(text);
        reject(new Error((res && (res.msg || res.message)) || '导出失败'));
      } catch (error) {
        reject(new Error('导出失败'));
      }
    };

    xhr.onerror = () => reject(new Error('网络异常'));
    xhr.send();
  });

export const UpdateSampleCouponAdmin = async (data: SampleCouponAdminSavePayload): Promise<any | null> => {
  return await Client.put<any>({
    url: apiPrefix + '/order/sample/admin/coupon/update',
    data,
  });
};

export const UpdateSampleCouponAdminStatus = async (data: SampleCouponAdminStatusPayload): Promise<any | null> => {
  return await Client.put<any>({
    url: apiPrefix + '/order/sample/admin/coupon/status',
    data,
  });
};

export const CreateNativePrepay = async (data: PayNativePrepayPayload): Promise<any | null> => {
  return await Client.post<PayNativePrepayResult>({
    url: apiPrefix + '/pay/native/prepay',
    data,
  });
};

export const GetPayOrderStatus = async (data: PayOrderStatusQuery): Promise<any | null> => {
  return await Client.get<PayOrderStatusResult>({
    url: apiPrefix + '/pay/status',
    data,
  });
};

export const ClosePayOrder = async (data: PayOrderClosePayload): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/pay/close',
    data,
  });
};
